import React, { useState, useEffect, useRef } from 'react';
import { Send, CheckCircle } from 'lucide-react';
import emailjs from '@emailjs/browser';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { eligibilityForm } from '../../data/mock';

// EmailJS Configuration
const EMAILJS_SERVICE_ID = 'service_nwvj5ol';
const EMAILJS_TEMPLATE_ID = 'template_6bt1xc9';
const EMAILJS_PUBLIC_KEY = 'XFlDnVrO2DWe8uVDW';

const EligibilityForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    propertyType: '',
    roomCount: '',
    prefecture: '',
    city: '',
    address: '',
    buildingName: '',
    currentStatus: '',
    message: '',
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState('');
  
  // Spam Protection: Honeypot field
  const [honeypot, setHoneypot] = useState('');
  
  // Spam Protection: Time-based check
  const formLoadTime = useRef(Date.now());
  
  // Reset form load time when component mounts
  useEffect(() => {
    formLoadTime.current = Date.now();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Spam Protection: Check honeypot field
    if (honeypot) {
      console.log('Spam detected: Honeypot field filled');
      // Silently reject - show fake success to confuse bots
      setIsSubmitted(true);
      return;
    }
    
    // Spam Protection: Check time elapsed (minimum 3 seconds)
    const timeElapsed = Date.now() - formLoadTime.current;
    if (timeElapsed < 3000) {
      console.log('Spam detected: Form submitted too quickly (' + timeElapsed + 'ms)');
      // Silently reject - show fake success to confuse bots
      setIsSubmitted(true);
      return;
    }
    
    setIsSubmitting(true);
    
    // Store email for redirect
    const userEmail = formData.email;
    
    try {
      // Get the base URL for the simulator
      const baseUrl = window.location.origin;
      const simulatorUrl = `${baseUrl}/simulator?email=${encodeURIComponent(userEmail)}`;
      
      // Auto-response email template (HTML) - Japanese
      const autoResponseMessage = `
<div style="font-family: 'Helvetica Neue', Arial, 'Hiragino Sans', 'Hiragino Kaku Gothic ProN', Meiryo, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; color: #1c1917;">
  <div style="text-align: center; padding-bottom: 20px; border-bottom: 1px solid #e7e5e4;">
    <h1 style="font-size: 24px; font-weight: 300; color: #292524; margin: 0;">ACOE</h1>
    <p style="font-size: 12px; color: #78716c; letter-spacing: 2px; margin-top: 5px;">LIFESTYLE ACCOMMODATION BRAND</p>
  </div>
  
  <div style="padding: 30px 0;">
    <p style="font-size: 16px; line-height: 1.8; margin-bottom: 20px;">
      ${formData.name} 様
    </p>
    
    <p style="font-size: 15px; line-height: 1.8; margin-bottom: 20px;">
      この度は、ACOEパートナーシップ適合性チェックに<br>
      お申し込みいただき、誠にありがとうございます。
    </p>
    
    <p style="font-size: 15px; line-height: 1.8; margin-bottom: 30px;">
      いただいた情報を確認の上、担当者より<br>
      <strong>2〜3営業日以内</strong>にご連絡させていただきます。
    </p>
    
    <div style="background: linear-gradient(135deg, #fef3c7 0%, #fffbeb 100%); border-radius: 12px; padding: 25px; margin: 30px 0;">
      <h2 style="font-size: 14px; color: #92400e; letter-spacing: 1px; margin: 0 0 15px 0; text-align: center;">
        【特典】収支シミュレーションツールのご案内
      </h2>
      <p style="font-size: 14px; line-height: 1.8; color: #57534e; margin-bottom: 20px; text-align: center;">
        下記ボタンより、あなた専用の<br>
        収支シミュレーションをご利用いただけます。
      </p>
      <div style="text-align: center; margin-bottom: 20px;">
        <a href="${simulatorUrl}" 
           style="display: inline-block; background: #292524; color: white; padding: 14px 32px; border-radius: 8px; text-decoration: none; font-size: 15px; font-weight: 500;">
          収支シミュレーションを開始する
        </a>
      </div>
      <div style="background: white; border-radius: 8px; padding: 20px; text-align: center;">
        <p style="font-size: 13px; color: #78716c; margin: 0 0 12px 0;">
          ログイン方法
        </p>
        <p style="font-size: 14px; color: #292524; margin: 0; line-height: 1.8;">
          ご登録いただいたメールアドレスと<br>
          パスコード「<strong>seirai@2026</strong>」で<br>
          ログインしてください。
        </p>
      </div>
      <p style="font-size: 12px; color: #78716c; margin: 15px 0 0 0; text-align: center;">
        ※ シミュレーション結果は、ご登録メールアドレスに<br>
        紐づいて安全に保存されます。
      </p>
    </div>
    
    <p style="font-size: 15px; line-height: 1.8; margin-bottom: 20px;">
      ご不明な点がございましたら、<br>
      お気軽にお問い合わせください。
    </p>
    
    <p style="font-size: 15px; line-height: 1.8;">
      今後ともACOEをよろしくお願いいたします。
    </p>
  </div>
  
  <div style="border-top: 1px solid #e7e5e4; padding-top: 20px; text-align: center;">
    <p style="font-size: 13px; color: #78716c; margin: 0;">
      <strong style="color: #292524;">ACOE</strong> | Lifestyle Accommodation Brand<br>
      <a href="mailto:office@seiraigroup.com" style="color: #b45309; text-decoration: none;">office@seiraigroup.com</a><br>
      <a href="https://www.acoe.jp/" style="color: #b45309; text-decoration: none;">https://www.acoe.jp/</a>
    </p>
  </div>
</div>
      `.trim();

      // Prepare form data for Web3Forms
      // Note: Web3Forms access key is safe to expose client-side as it's designed for this purpose
      const web3FormData = {
        access_key: '44155a65-9c14-48e1-b409-fc7b865136f9',
        subject: '【ACOE】新規パートナーシップ適合性チェック申請',
        from_name: 'ACOE ウェブサイト',
        // IMPORTANT: 'email' field is required for Web3Forms auto-response to work
        email: formData.email,
        replyto: formData.email,
        // Auto-response configuration
        autoresponse: true,
        autoresponse_subject: '【ACOE】お申し込みありがとうございます - シミュレーションのご案内',
        autoresponse_from: 'ACOE',
        autoresponse_message: autoResponseMessage,
        // Form fields (Japanese labels for your notification email)
        'お名前': formData.name,
        '電話番号': formData.phone,
        '物件タイプ': formData.propertyType,
        '部屋数': formData.roomCount,
        '都道府県': formData.prefecture,
        '市区町村': formData.city,
        '住所': formData.address,
        '建物名': formData.buildingName || 'なし',
        '現在の状況': formData.currentStatus,
        'メッセージ': formData.message || 'なし',
      };

      console.log('Submitting form to Web3Forms...', web3FormData);

      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(web3FormData)
      });

      const result = await response.json();
      console.log('Web3Forms response:', result);
      
      if (result.success) {
        console.log('Web3Forms submission successful, sending auto-response via EmailJS...');
        
        // Send auto-response email to user via EmailJS
        try {
          const emailParams = {
            to_email: userEmail,
            to_name: formData.name,
            simulator_url: simulatorUrl,
            passcode: 'seirai@2026',
          };
          
          await emailjs.send(
            EMAILJS_SERVICE_ID,
            EMAILJS_TEMPLATE_ID,
            emailParams,
            EMAILJS_PUBLIC_KEY
          );
          console.log('Auto-response email sent successfully via EmailJS');
        } catch (emailError) {
          // Log error but don't block form submission success
          console.error('EmailJS auto-response failed:', emailError);
        }
        
        // Store the simulator URL for the success page button
        setSubmittedEmail(userEmail);
        setIsSubmitted(true);
      } else {
        console.error('Form submission failed:', result);
        alert('送信に失敗しました: ' + (result.message || 'もう一度お試しください。'));
      }
    } catch (error) {
      console.error('Form submission error:', error);
      alert('送信中にエラーが発生しました。もう一度お試しください。');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    const simulatorUrl = `/simulator?email=${encodeURIComponent(submittedEmail)}`;
    
    return (
      <section id="contact" className="py-16 lg:py-20 bg-gradient-to-b from-amber-50/50 to-stone-50">
        <div className="max-w-2xl mx-auto px-6 lg:px-8 text-center">
          <div className="bg-white rounded-3xl p-12 shadow-sm border border-stone-100">
            <div className="w-20 h-20 mx-auto mb-8 rounded-full bg-green-100 flex items-center justify-center">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h3 className="text-2xl font-light text-stone-800 mb-4">
              お申し込みありがとうございます
            </h3>
            <p className="text-stone-600 font-light mb-8">
              いただいた情報を確認の上、<br />
              2〜3営業日以内にご連絡いたします。
            </p>
            
            {/* Big Button to Simulator - Opens in new tab */}
            <a
              href={simulatorUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block w-full max-w-md bg-stone-800 hover:bg-stone-700 text-white text-lg font-medium py-5 px-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-[1.02]"
            >
              収支シミュレーションアプリへ
            </a>
            
            {/* Login Instructions */}
            <div className="mt-8 p-6 bg-amber-50/50 rounded-xl border border-amber-200/50">
              <p className="text-amber-800 text-sm font-medium mb-3">
                ━━━ ログイン方法 ━━━
              </p>
              <p className="text-stone-700 text-sm leading-relaxed">
                ご登録いただいたメールアドレスと<br />
                パスコード「<span className="font-semibold text-stone-900">seirai@2026</span>」で<br />
                ログインしてください。
              </p>
              <p className="text-stone-500 text-xs mt-4">
                ※ シミュレーション結果は、ご登録メールアドレスに<br />
                紐づいて安全に保存されます。
              </p>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="contact" className="py-16 lg:py-20 bg-gradient-to-b from-amber-50/50 to-stone-50">
      <div className="max-w-3xl mx-auto px-6 lg:px-8">
        {/* Section header */}
        <div className="text-center mb-12">
          <p className="text-amber-800/80 text-base tracking-[0.2em] uppercase mb-4">
            {eligibilityForm.titleEn}
          </p>
          <h2 className="text-4xl md:text-5xl font-light text-stone-800 mb-6">
            {eligibilityForm.title}
          </h2>
          <div className="w-20 h-px bg-amber-700/40 mx-auto mb-8" />
          <p className="text-lg text-stone-600 font-light leading-relaxed">
            {eligibilityForm.subtitle}
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-3xl p-8 lg:p-12 shadow-sm border border-stone-100">
          {/* Honeypot field - Hidden from users, only bots fill this */}
          <div style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0, overflow: 'hidden' }} aria-hidden="true">
            <label htmlFor="website">Website</label>
            <input
              type="text"
              id="website"
              name="website"
              value={honeypot}
              onChange={(e) => setHoneypot(e.target.value)}
              tabIndex={-1}
              autoComplete="off"
            />
          </div>
          
          <div className="grid gap-6">
            {/* Name & Email */}
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-stone-700">
                  お名前 <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="border-stone-200 focus:border-amber-400 focus:ring-amber-400/20"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email" className="text-stone-700">
                  メールアドレス <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="border-stone-200 focus:border-amber-400 focus:ring-amber-400/20"
                />
              </div>
            </div>

            {/* Phone */}
            <div className="space-y-2">
              <Label htmlFor="phone" className="text-stone-700">
                電話番号
              </Label>
              <Input
                id="phone"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleInputChange}
                className="border-stone-200 focus:border-amber-400 focus:ring-amber-400/20"
              />
            </div>

            {/* Property Type & Room Count */}
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label className="text-stone-700">
                  物件タイプ <span className="text-red-500">*</span>
                </Label>
                <Select
                  value={formData.propertyType}
                  onValueChange={(value) => handleSelectChange('propertyType', value)}
                  required
                >
                  <SelectTrigger className="border-stone-200 focus:border-amber-400 focus:ring-amber-400/20">
                    <SelectValue placeholder="選択してください" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="building">一棟ビル</SelectItem>
                    <SelectItem value="apartment">マンション複数室</SelectItem>
                    <SelectItem value="house">戸建て</SelectItem>
                    <SelectItem value="other">その他</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-stone-700">
                  部屋数 <span className="text-red-500">*</span>
                </Label>
                <Select
                  value={formData.roomCount}
                  onValueChange={(value) => handleSelectChange('roomCount', value)}
                  required
                >
                  <SelectTrigger className="border-stone-200 focus:border-amber-400 focus:ring-amber-400/20">
                    <SelectValue placeholder="選択してください" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="under5">5室未満</SelectItem>
                    <SelectItem value="5to10">5〜10室</SelectItem>
                    <SelectItem value="11to20">11〜20室</SelectItem>
                    <SelectItem value="21to30">21〜30室</SelectItem>
                    <SelectItem value="over30">30室以上</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Property Location Section */}
            <div className="pt-4 border-t border-stone-100">
              <Label className="text-stone-700 text-base font-medium mb-4 block">
                物件所在地
              </Label>
              
              {/* Prefecture & City */}
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div className="space-y-2">
                  <Label htmlFor="prefecture" className="text-stone-700">
                    都道府県 <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="prefecture"
                    name="prefecture"
                    value={formData.prefecture}
                    onChange={handleInputChange}
                    placeholder="例: 東京都"
                    required
                    className="border-stone-200 focus:border-amber-400 focus:ring-amber-400/20"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="city" className="text-stone-700">
                    市区町村 <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="city"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    placeholder="例: 新宿区"
                    required
                    className="border-stone-200 focus:border-amber-400 focus:ring-amber-400/20"
                  />
                </div>
              </div>

              {/* Address & Building Name */}
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="address" className="text-stone-700">
                    町名・番地 <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="address"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    placeholder="例: 西新宿1-2-3"
                    required
                    className="border-stone-200 focus:border-amber-400 focus:ring-amber-400/20"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="buildingName" className="text-stone-700">
                    ビル名・マンション名
                  </Label>
                  <Input
                    id="buildingName"
                    name="buildingName"
                    value={formData.buildingName}
                    onChange={handleInputChange}
                    placeholder="例: ACOEビル 5階"
                    className="border-stone-200 focus:border-amber-400 focus:ring-amber-400/20"
                  />
                </div>
              </div>
            </div>

            {/* Current Status */}
            <div className="space-y-2">
              <Label className="text-stone-700">
                現在の運営状況 <span className="text-red-500">*</span>
              </Label>
              <Select
                value={formData.currentStatus}
                onValueChange={(value) => handleSelectChange('currentStatus', value)}
                required
              >
                <SelectTrigger className="border-stone-200 focus:border-amber-400 focus:ring-amber-400/20">
                  <SelectValue placeholder="選択してください" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="not_started">未運営（これから開始）</SelectItem>
                  <SelectItem value="self_managed">自主運営中</SelectItem>
                  <SelectItem value="outsourced">他社委託中</SelectItem>
                  <SelectItem value="paused">休止中</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Message */}
            <div className="space-y-2">
              <Label htmlFor="message" className="text-stone-700">
                ご質問・補足事項
              </Label>
              <Textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                rows={4}
                className="border-stone-200 focus:border-amber-400 focus:ring-amber-400/20 resize-none"
              />
            </div>

            {/* Note */}
            <p className="text-stone-500 text-sm font-light">
              {eligibilityForm.note}
            </p>

            {/* Submit button */}
            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-stone-800 hover:bg-stone-700 text-stone-50 py-6 text-base font-normal tracking-wide transition-all button-hover disabled:opacity-50"
            >
              {isSubmitting ? (
                <span className="flex items-center gap-2">
                  <div className="w-5 h-5 border-2 border-stone-300 border-t-white rounded-full animate-spin" />
                  送信中...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  適合性を確認する
                  <Send className="w-4 h-4" />
                </span>
              )}
            </Button>
          </div>
        </form>
      </div>
    </section>
  );
};

export default EligibilityForm;
